import type { Span } from '@opentelemetry/api';
import { context, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';
import type { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-base';
import {
  BasicTracerProvider,
  SimpleSpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import { getBuildInfo } from './wide-events';

// Wide Event exporter that converts OpenTelemetry spans to business-focused wide events
export class WideEventExporter implements SpanExporter {
  // Store child spans until their parent is exported
  private childSpanCache = new Map<string, ReadableSpan[]>();

  // biome-ignore lint/suspicious/noExplicitAny: do not know the type of the result
  export(spans: ReadableSpan[], resultCallback: (result: any) => void): void {
    spans.forEach((span) => {
      if (span.parentSpanId) {
        // This is a child span - store it for when parent is exported
        const parentId = span.parentSpanId;
        if (!this.childSpanCache.has(parentId)) {
          this.childSpanCache.set(parentId, []);
        }
        this.childSpanCache.get(parentId)?.push(span);
      } else {
        // This is a root span - emit wide event with any cached children
        const spanId = span.spanContext().spanId;
        const childSpans = this.childSpanCache.get(spanId) || [];
        const allSpans = [span, ...childSpans];

        const wideEvent = this.spanToWideEvent(span, allSpans);

        // Determine log level based on span status and HTTP status
        const success = this.isSuccessfulSpan(span);

        const logEntry = {
          level: success ? 'info' : 'error',
          summary: this.generateSummary(span, wideEvent),
          performance_category: this.categorizePerformance(
            wideEvent['timing.total_ms']
          ),
          error_category: success
            ? null
            : this.categorizeError(span, wideEvent),
          ...wideEvent,
        };

        // Emit with appropriate log level
        if (success) {
          console.log(logEntry);
        } else {
          console.error(logEntry);
        }

        // Clean up cached child spans for this parent
        this.childSpanCache.delete(spanId);
      }
    });

    resultCallback({ code: 0 /* SUCCESS */ });
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  private isSuccessfulSpan(span: ReadableSpan): boolean {
    // Check HTTP status code first
    const httpStatus = span.attributes['http.status_code'] as number;
    if (httpStatus) {
      return httpStatus >= 200 && httpStatus < 400;
    }

    // Fall back to span status
    return span.status.code !== SpanStatusCode.ERROR;
  }

  private spanToWideEvent(rootSpan: ReadableSpan, allSpans: ReadableSpan[]) {
    const attributes = rootSpan.attributes || {};
    const childSpans = allSpans.filter(
      (span) => span.parentSpanId === rootSpan.spanContext().spanId
    );

    // Calculate timing from child spans for backward compatibility
    const timing = this.extractLegacyTiming(childSpans);

    // Calculate total duration
    const totalDurationMs = this.calculateDuration(rootSpan);

    // Extract business context and HTTP context separately
    const httpContext = this.extractHttpContext(attributes);
    const businessContext = this.extractBusinessContext(attributes);
    const traceContext = this.extractTraceContext(rootSpan);

    return {
      // Request and operation context
      request_id: attributes['request.id'] as string,
      timestamp: new Date(
        rootSpan.startTime[0] * 1000 + rootSpan.startTime[1] / 1000000
      ).toISOString(),
      operation: attributes['operation.type'] as string,
      success: this.isSuccessfulSpan(rootSpan),

      // HTTP context
      ...httpContext,

      // Trace context
      ...traceContext,

      // Business context (user, resource, domain-specific data)
      ...businessContext,

      // Legacy timing for backward compatibility
      ...timing,
      'timing.total_ms': totalDurationMs,

      // Comprehensive span information
      spans: this.formatSpansHierarchy(rootSpan, allSpans),

      // Build information
      ...getBuildInfo(),

      // Error context if applicable
      ...(rootSpan.status.code === SpanStatusCode.ERROR && {
        error: this.extractDetailedError(attributes, rootSpan),
      }),
    };
  }

  // biome-ignore lint/suspicious/noExplicitAny: do not know the type of the result
  private extractHttpContext(attributes: Record<string, any>) {
    const httpKeys = [
      'http.method',
      'http.url',
      'http.scheme',
      'http.host',
      'http.target',
      'http.status_code',
      'http.status_text',
      'http.user_agent',
      'http.request_content_length',
      'http.response_content_length',
    ];

    // biome-ignore lint/suspicious/noExplicitAny: record values can be anything
    const httpContext: Record<string, any> = {};
    httpKeys.forEach((key) => {
      if (attributes[key] !== undefined) {
        httpContext[key] = attributes[key];
      }
    });

    // Add path from target for legacy compatibility
    if (attributes['http.target']) {
      httpContext['http.path'] = new URL(
        attributes['http.target'] as string,
        'http://localhost'
      ).pathname;
    }

    return httpContext;
  }

  // biome-ignore lint/suspicious/noExplicitAny: do not know the type of the result
  private extractBusinessContext(attributes: Record<string, any>) {
    // Import the config manager to get the current context schema
    const { observabilityConfig } = require('./config');
    const businessKeys = observabilityConfig.getAllContextFields();

    // biome-ignore lint/suspicious/noExplicitAny: do not know the type of the result
    const businessContext: Record<string, any> = {};
    businessKeys.forEach((key: string) => {
      if (attributes[key] !== undefined) {
        businessContext[key] = attributes[key];
      }
    });

    return businessContext;
  }

  private extractTraceContext(span: ReadableSpan) {
    return {
      'trace.trace_id': span.spanContext().traceId,
      'trace.span_id': span.spanContext().spanId,
      ...(span.parentSpanId && { 'trace.parent_span_id': span.parentSpanId }),
    };
  }

  private extractLegacyTiming(childSpans: ReadableSpan[]) {
    const timing: Record<string, number> = {};

    childSpans.forEach((span) => {
      const spanType = span.attributes['operation.layer'] as string;
      if (spanType) {
        const duration = this.calculateDuration(span);
        timing[`timing.${spanType}_ms`] = duration;
      }
    });

    return timing;
  }

  private calculateDuration(span: ReadableSpan): number {
    if (!span.endTime) return 0;

    const startMs = span.startTime[0] * 1000 + span.startTime[1] / 1000000;
    const endMs = span.endTime[0] * 1000 + span.endTime[1] / 1000000;
    return Math.round((endMs - startMs) * 100) / 100;
  }

  private formatSpansHierarchy(
    _rootSpan: ReadableSpan,
    allSpans: ReadableSpan[]
  ) {
    return allSpans.map((span) => ({
      name: span.name,
      span_id: span.spanContext().spanId,
      parent_span_id: span.parentSpanId || undefined,
      start_time: new Date(
        span.startTime[0] * 1000 + span.startTime[1] / 1000000
      ).toISOString(),
      end_time: span.endTime
        ? new Date(
            span.endTime[0] * 1000 + span.endTime[1] / 1000000
          ).toISOString()
        : undefined,
      duration_ms: this.calculateDuration(span),
      status: span.status.code === SpanStatusCode.ERROR ? 'error' : 'ok',
      attributes: { ...span.attributes },
      events: span.events.map((event) => ({
        timestamp: new Date(
          event.time[0] * 1000 + event.time[1] / 1000000
        ).toISOString(),
        name: event.name,
        attributes: event.attributes || {},
      })),
    }));
  }

  // biome-ignore lint/suspicious/noExplicitAny: do not know the type of the result
  private generateSummary(span: ReadableSpan, wideEvent: any): string {
    const summaryParts: string[] = [];
    if (wideEvent.request_id) {
      summaryParts.push(`Request ID: ${wideEvent.request_id}`);
    }
    if (wideEvent.operation) {
      summaryParts.push(`Operation: ${wideEvent.operation}`);
    }
    if (wideEvent['timing.total_ms']) {
      summaryParts.push(`Duration: ${wideEvent['timing.total_ms']}ms`);
    }
    if (span.status.code === SpanStatusCode.ERROR) {
      summaryParts.push(`Error: ${span.status.message || 'Unknown error'}`);
    }
    return summaryParts.join(' - ');
  }

  private categorizePerformance(durationMs: number): string | null {
    if (durationMs < 100) {
      return 'fast';
    }
    if (durationMs < 1000) {
      return 'moderate';
    }
    return 'slow';
  }

  private extractDetailedError(
    // biome-ignore lint/suspicious/noExplicitAny: the attributes could be anything
    attributes: Record<string, any>,
    span: ReadableSpan
    // biome-ignore lint/suspicious/noExplicitAny: the result could be anything
  ): Record<string, any> {
    // Check if we have detailed error attributes from trackError()
    if (attributes['error.type']) {
      // biome-ignore lint/suspicious/noExplicitAny: the result could be anything
      const error: Record<string, any> = {
        type: attributes['error.type'],
        message:
          attributes['error.message'] || span.status.message || 'Unknown error',
      };

      // Add validation details if present
      if (attributes['error.validation.issue_count']) {
        error.validation = {
          issueCount: attributes['error.validation.issue_count'],
          fields: attributes['error.validation.fields']
            ? attributes['error.validation.fields'].split(',')
            : [],
        };
      }

      // Add business rule details if present
      if (attributes['error.business_rule.rule']) {
        error.businessRule = {
          rule: attributes['error.business_rule.rule'],
          violation: attributes['error.business_rule.violation'],
        };
      }

      // Add unexpected flag if present
      if (attributes['error.unexpected'] !== undefined) {
        error.unexpected = attributes['error.unexpected'];
      }

      return error;
    }

    // Fall back to generic span status error
    return {
      type: 'SpanError',
      message: span.status.message || 'Unknown error',
    };
  }

  // biome-ignore lint/suspicious/noExplicitAny: do not know the type of the result
  private categorizeError(span: ReadableSpan, wideEvent: any): string | null {
    if (span.status.code === SpanStatusCode.ERROR) {
      if (wideEvent['error.validation.issue_count']) {
        return 'validation_error';
      }
      if (wideEvent['error.business_rule.violation'] === 'already_exists') {
        return 'already_exists';
      }
      if (wideEvent['error.unexpected']) {
        return 'unexpected_error';
      }
      return 'application_error';
    }
    return null;
  }
}

// Global TracerProvider instance
let tracerProvider: BasicTracerProvider | null = null;

// Initialize OpenTelemetry with proper wide event exporter
export function initializeTracing() {
  // Only initialize once
  if (tracerProvider) {
    return;
  }

  console.log(
    'OpenTelemetry tracing initialized with build info:',
    getBuildInfo()['service.name'],
    getBuildInfo()['git.commit_hash_short']
  );

  // Create Wide Event exporter with immediate processing for development
  const wideEventExporter = new WideEventExporter();

  // Use SimpleSpanProcessor for immediate export (better for Cloudflare Workers)
  const spanProcessor = new SimpleSpanProcessor(wideEventExporter);

  // Create TracerProvider with processor in config
  tracerProvider = new BasicTracerProvider({
    spanProcessors: [spanProcessor],
  });
  tracerProvider.register();

  console.log('Wide Events exporter configured and ready');
}

// Utility functions for creating spans (simplified, let OTel handle the complexity)
export interface TraceOptions {
  name: string;
  kind?: SpanKind;
  attributes?: Record<string, string | number | boolean>;
  parentSpan?: Span;
}

export function createSpan(options: TraceOptions): Span {
  const tracer = trace.getTracer(
    'delta-base-auth',
    String(getBuildInfo()['service.version'])
  );

  const span = options.parentSpan
    ? tracer.startSpan(
        options.name,
        {
          kind: options.kind || SpanKind.INTERNAL,
          attributes: options.attributes,
        },
        trace.setSpan(context.active(), options.parentSpan)
      )
    : tracer.startSpan(options.name, {
        kind: options.kind || SpanKind.INTERNAL,
        attributes: options.attributes,
      });

  return span;
}

export function withSpan<T>(
  span: Span,
  fn: (span: Span) => Promise<T>
): Promise<T> {
  return context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const result = await fn(span);
      span.setStatus({ code: SpanStatusCode.OK });
      return result;
    } catch (error) {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error instanceof Error ? error.message : String(error),
      });
      span.recordException(
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    } finally {
      span.end();
    }
  });
}
